<script setup lang="ts">
import type { Schemas } from "#shopware"
import { buildUrlPrefix, getProductRoute } from "@shopware/helpers"

const router = useRouter()
const { getUrlPrefix } = useUrlResolver()

const prefix = getUrlPrefix()
const props = withDefaults(
    defineProps<{
        configurator: Schemas["PropertyGroup"][] | null,
        parentId: string | undefined,
        allowRedirect?: boolean,
        currentProduct?: Schemas["Product"] | null
    }>(),
    {
        allowRedirect: false,
        currentProduct: null
    }
)

const {
    handleChange,
    getSelectedOptions,
    findVariantForSelectedOptions,
} = useProductConfigurator()
const { search } = useProductSearch()
const { apiClient } = useShopwareContext()

type OptionStates = 'selected' | 'combinable' | 'uncombinable' | 'unavailable' | 'disabled' | 'hidden' | 'notMapped' | null
interface OptionInfo { id: string, groupId: string }
type CompatIndex = Record<string, Set<string>>

const emit = defineEmits<{
    "productVariantChanged": [product: Schemas["Product"]],
    "variantNotFound": []
}>()
const isLoading = ref(false)

const onHandleChange = async () => {
    isLoading.value = true

    try {
        const variantFound = await findVariantForSelectedOptions()

        if (variantFound) {
            const newProductVariant = await search(variantFound.id)
            emit('productVariantChanged', newProductVariant.product)

            if (props.allowRedirect) {
                const selectedOptionsVariantPath = buildUrlPrefix(
                    getProductRoute(variantFound),
                    prefix,
                )

                if (selectedOptionsVariantPath) {
                    try {
                        await router.push(selectedOptionsVariantPath)
                    } catch (error) {
                        console.error('Navigation failed for path:', selectedOptionsVariantPath, error)
                    }
                }
            }

            defaultVariantOptionIds.value = Object.values(getSelectedOptions.value) as string[]
            compatibilityMap.value = buildGroupCompat(defaultVariantOptionIds.value, compat, info)

        } else {
            // No variant found for the selected options combination
            emit('variantNotFound')
        }
    } finally {
        isLoading.value = false
    }
}

/* -------------------------------------------------
 * Logic for selectable options
 * ------------------------------------------------- */
const loadBuyableVariants = async (): Promise<Schemas["Product"][]> => {
    const parentId =
        props.parentId
        ?? null
    const response = await apiClient.invoke(
        "readProduct post /product",
        {
            pathParams: {
                productId: parentId },
            body: {
                filter: [
                    { type: "equals", field: "parentId", value: parentId },
                    { type: "equals", field: "active",   value: true }
                ],
                associations: { options: { associations: { group: {} } }, properties: { associations: { group: {} } } },
                includes: { product: ["id", "optionIds", "options", "properties", "stock", "availableStock", "isCloseout"] },
                limit: 100
            }

        }
    )
    return response.data.elements as Schemas["Product"][]
}

function indexVariants (variants: Schemas['Product'][]) {
    const compat: CompatIndex = {}
    const info: Record<string, OptionInfo> = {}
    const availableOptionIds = new Set<string>()
    const closeoutOptionIds = new Set<string>()
    const buyableVariants: Schemas['Product'][] = []

    for (const variant of variants) {
        if (variant.options) {
            const stock = variant.availableStock ?? variant.stock ?? 0
            const hasHalfSizeName = variant.options.some(
                (opt: Schemas["PropertyGroupOption"]) => /\d+½/.test(opt.translated?.name || '')
            )
            const isBuyable = !(hasHalfSizeName && stock === 0)

            if (isBuyable) {
                buyableVariants.push(variant)
            }

            const ids = variant.options.map((o: Schemas["PropertyGroupOption"]) => o.id)

            for (const opt of variant.options) {
                info[opt.id] = { id: opt.id, groupId: opt.groupId }

                if (isBuyable) {
                    availableOptionIds.add(opt.id)
                    const bucket = (compat[opt.id] ??= new Set<string>())
                    ids.forEach((id: string) => id !== opt.id && bucket.add(id))
                }

                if (variant.isCloseout && stock === 0 && !hasHalfSizeName) {
                    closeoutOptionIds.add(opt.id)
                }
            }
        }
    }
    return { compat, info, availableOptionIds, closeoutOptionIds, buyableVariants }
}

function buildGroupCompat (
    selectedOptionIds: string[],
    compat: CompatIndex,
    info: Record<string, OptionInfo>
) {
    const result: Record<string, string[]> = {}
    const seen: Record<string, Set<string>> = {}

    for (const selectedId of selectedOptionIds) {
        compat[selectedId]?.forEach(otherId => {
            const groupId = info[otherId]?.groupId
            if (groupId) {
                (seen[groupId] ??= new Set()).add(otherId)
            }
        })
    }

    Object.entries(seen).forEach(([groupId, set]) => {
        result[groupId] = [...set]
    })
    return result
}

const allVariants = await loadBuyableVariants()
const hasColorVariantGroups = computed(() => {
    return props.configurator?.some(group => group.displayType === 'color') ?? false
})

const compatibilityMap = ref<Record<string, string[]>>({})  // { groupId: [available optionIds] }
const { compat, info, availableOptionIds, closeoutOptionIds, buyableVariants: variants } = indexVariants(allVariants)
const defaultVariantOptionIds = ref(Object.values(getSelectedOptions.value) as string[])
compatibilityMap.value = buildGroupCompat(defaultVariantOptionIds.value, compat, info)

/* -------------------------------------------------
 * EU/UK size label mapping from variant properties
 * ------------------------------------------------- */
const manufacturerSizeSystem = (props.currentProduct?.manufacturer?.customFields as unknown as Record<string, unknown>)?.gisy_manufacturer_size_system as string | null | undefined
const { hasUkSizes, showUkSizes, toggleSizeMode, getDisplayLabel, getCombinedLabel } = useMappedSizeLabels(variants, manufacturerSizeSystem)


/* -------------------------------------------------
 * Logic for styling of variant group options
 * ------------------------------------------------- */
function optionState (group: Schemas["PropertyGroup"], option: Schemas["PropertyGroupOption"]): OptionStates {
    const selected = getSelectedOptions.value[group.name] === option.id
    const isAvailable = availableOptionIds.has(option.id)
    const isCloseout = closeoutOptionIds.has(option.id)
    const combinable = compatibilityMap.value[group.id]?.includes(option.id)
    const displaySize = getDisplayLabel(option)

    if (selected && displaySize !== '-') {
        return 'selected'
    }

    // Check if option is available in any buyable variant
    if (!isAvailable) {
        return 'hidden'
    }

    if (isCloseout) {
        return 'unavailable'
    }

    if (combinable) {
        return 'combinable'
    }

    if (displaySize === '-') {
        return 'notMapped'
    }

    if (!combinable && !selected) {
        // if product has only 1 variant option group the compatibility map is empty
        if (Object.keys(compatibilityMap.value).length <= 0) {
            return 'combinable'
        } else {
            return 'uncombinable'
        }
    }

    return null
}

function optionStyles(state: OptionStates): string {
    const focusClass = ' focus-style'
    const borderClass = ' border'
    const borderSelected = 'border-tertiary'

    switch (state) {
    case 'selected':
        return [
            borderClass,
            borderSelected,
            'cursor-pointer text-neutral bg-primary/10',
            focusClass
        ].join(' ').trim()

    case 'combinable':
        return [
            'cursor-pointer',
            focusClass
        ].join(' ').trim()

    case 'unavailable':
        return [
            'text-neutral/40',
            'cursor-not-allowed',
            'opacity-50',
            focusClass
        ].join(' ').trim()

    case 'hidden':
        return [
            'hidden'
        ].join(' ').trim()

    case 'uncombinable':
        return [
            'text-neutral/40',
            'cursor-not-allowed',
            'opacity-60',
            focusClass
        ].join(' ').trim()

    case 'notMapped':
        return [
            'text-neutral/40',
            'cursor-not-allowed',
            'opacity-60',
            focusClass
        ].join(' ').trim()

    default:
        return ''
    }
}

// Get selected option name for a variant group
function getSelectedOptionName(group: Schemas["PropertyGroup"]): string | null {
    const selectedOptionId = getSelectedOptions.value[group.name]
    if (!selectedOptionId) return null

    const selectedOption = group.options?.find((option: Schemas["PropertyGroupOption"]) => option.id === selectedOptionId)
    if (!selectedOption) return null

    if (hasUkSizes) return getCombinedLabel(selectedOption)
    return getDisplayLabel(selectedOption)
}

// Check if current product has low stock (< 4)
const hasLowStock = computed(() => {
    if (!props.currentProduct) return false
    const stock = props.currentProduct.availableStock ?? props.currentProduct.stock ?? 0
    return stock > 0 && stock < 4
})
</script>

<template>
    <section v-if="props.configurator != null && props.configurator.length > 0" :aria-label="$t('product.detail.variants')" class="relative flex flex-col gap-6">
        <div
            v-if="isLoading"
            class="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-white/75 z-10"
        >
            <div class="w-4 h-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
        </div>

        <div v-for="variantGroup in props.configurator" :key="variantGroup.id">
            <LazyProductDetailVariantSwatchGroup
                v-if="hasColorVariantGroups"
                :variant-group="variantGroup"
                :variants="variants"
                :option-state="optionState"
                :handle-change="handleChange"
                :on-handle-change="onHandleChange"
            />
            <div v-else>
                <!-- Low stock warning -->
                <div
                    v-if="hasLowStock"
                    class="mb-6"
                >
                    <span>{{ $t('product.availableStock') }}</span>
                </div>

                <div class="font-bold mb-2">
                    {{ variantGroup.translated.name }} {{ $t('product.detail.choose') }}:
                    <span v-if="getSelectedOptionName(variantGroup)" class="font-normal text-primary">
                        {{ getSelectedOptionName(variantGroup) }}
                    </span>
                </div>

                <!-- Size Toggle Button -->
                <div v-if="hasUkSizes" class="flex justify-start my-3">
                    <button
                        type="button"
                        class="text-primary underline text-sm cursor-pointer"
                        @click="toggleSizeMode"
                    >
                        {{ !showUkSizes ? $t('product.detail.sizeToggleUK') : $t('product.detail.sizeToggleEU') }}
                    </button>
                </div>

                <fieldset class="flex flex-wrap justify-start gap-2">
                    <legend class="sr-only">
                        {{ $t('product.detail.chooseOption') }} {{ variantGroup.translated.name }}
                    </legend>

                    <div class="flex flex-wrap gap-3">
                        <template v-for="variantOption in variantGroup.options" :key="variantOption.id">
                            <FoundationLabel
                                :tabindex="['uncombinable', 'unavailable'].includes(optionState(variantGroup, variantOption) || '') ? -1 : 0"
                                :aria-label="`${variantGroup.translated.name}: ${getDisplayLabel(variantOption)}`"
                                :aria-disabled="['uncombinable', 'unavailable'].includes(optionState(variantGroup, variantOption) || '') ? 'true' : 'false'"
                                class="relative flex-shrink-0 flex justify-center items-center min-h-[45px] px-6 focus-style min-w-20"
                                :class="optionStyles(optionState(variantGroup, variantOption))"
                                @keydown.enter.prevent="!['uncombinable', 'unavailable'].includes(optionState(variantGroup, variantOption) || '') && handleChange(variantGroup.translated.name, variantOption.id, onHandleChange)"
                                @keydown.space.prevent="!['uncombinable', 'unavailable'].includes(optionState(variantGroup, variantOption) || '') && handleChange(variantGroup.translated.name, variantOption.id, onHandleChange)"
                            >
                                <span>{{ getDisplayLabel(variantOption) }}</span>
                                <input
                                    type="radio"
                                    :name="variantGroup.id"
                                    :value="variantOption.id"
                                    :checked="getSelectedOptions[variantGroup.translated.name] === variantOption.id"
                                    :disabled="['uncombinable', 'unavailable'].includes(optionState(variantGroup, variantOption) || '')"
                                    :aria-describedby="`${variantOption.id}-description`"
                                    class="sr-only"
                                    @click="!['uncombinable', 'unavailable'].includes(optionState(variantGroup, variantOption) || '') && handleChange(variantGroup.translated.name, variantOption.id, onHandleChange)"
                                >
                                <span
                                    :id="`${variantOption.id}-description`"
                                    class="sr-only"
                                >
                                    {{ optionState(variantGroup, variantOption) === 'selected' ? $t('product.detail.selected') : '' }}
                                </span>
                            </FoundationLabel>
                        </template>
                    </div>
                </fieldset>
            </div>
        </div>
    </section>
</template>